import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Popup from '../Popup';
import { ScoreUpdateRequestTypes } from '../../utils/constants';
import { getApiAxiosInstance } from '../../utils/network';
import { submitScoreUpdateRequest } from '../../api';
import { getIncorrectScoreMessage, shouldShowSourceUrlInput, SCORE_UPDATE_SUCCESS_COPY } from './scoreUpdateCopy';
import { validateSourceUrl } from './validateSourceUrl';

import styles from './scoreUpdateRequest.scss';

const POPUP_CONFIG = {
    fullScreen: false,
    noPadding: true,
    gradient: true,
    scrollable: false,
    noHeader: true,
};

const SOURCE_URL_PLACEHOLDER = 'Paste URL here (e.g., https://example.com)';
const SUBMIT_FAILURE_MESSAGE = 'We could not send your report. Please try again.';

// Owns the request modals and the submit call, so the two surfaces that offer this action - the
// profile Results tab score card and the imported event match card - cannot drift apart. The
// caller owns only whether the flow is open, because on the profile it is one item in a menu of
// several actions whose state that card already tracks.
const ScoreUpdateRequest = ({
    isOpen, resultId, dataImportTypeId, onClose, onRequested, 'data-testid': dataTestId,
}) => {
    const [sourceUrl, setSourceUrl] = useState('');
    const [sourceUrlError, setSourceUrlError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const showUrlInput = shouldShowSourceUrlInput(dataImportTypeId);
    const message = getIncorrectScoreMessage(dataImportTypeId);
    const isSubmitDisabled = showUrlInput && (!sourceUrl?.trim() || !!sourceUrlError);

    const resetRequestState = () => {
        setSourceUrl('');
        setSourceUrlError('');
        setSubmitError('');
        setIsSubmitting(false);
    };

    const handleClose = () => {
        resetRequestState();
        onClose();
    };

    const handleUrlChange = (event) => {
        const url = event.target.value;
        setSourceUrl(url);
        setSourceUrlError(validateSourceUrl(url));
    };

    const handleSubmit = async () => {
        if (isSubmitting || isSubmitDisabled) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const requestData = {
                resultId,
                requestType: ScoreUpdateRequestTypes.INCORRECT_SCORE,
            };

            if (sourceUrl) {
                requestData.sourceUrl = sourceUrl;
            }

            await submitScoreUpdateRequest(getApiAxiosInstance(), requestData);

            resetRequestState();
            setShowSuccess(true);
            onClose();

            if (onRequested) {
                onRequested(resultId);
            }
        } catch {
            // The binding is dropped on purpose: SUBMIT_FAILURE_MESSAGE is fixed copy, so no backend
            // string reaches the user. The modal stays open with the URL still in the field, making
            // a retry a click rather than a retype.
            setSubmitError(SUBMIT_FAILURE_MESSAGE);
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isOpen && (
                <Popup
                    className={`popup-md ${styles.popupConfig}`}
                    data-testid={dataTestId && `${dataTestId}.incorrect-score`}
                    onCloseClick={handleClose}
                    config={POPUP_CONFIG}
                >
                    <div className={styles.popupContainer}>
                        <p className={styles.popupHeader} data-testid={dataTestId && `${dataTestId}.incorrect-score.header`}>
                            {message.header}
                        </p>
                        <p className={styles.popupDetails} data-testid={dataTestId && `${dataTestId}.incorrect-score.details`}>
                            {message.details}
                        </p>

                        {showUrlInput && (
                            <div className={styles.inputContainer}>
                                <input
                                    type="url"
                                    className={`form-control ${styles.inputField} ${sourceUrlError ? styles.inputError : ''}`}
                                    placeholder={SOURCE_URL_PLACEHOLDER}
                                    value={sourceUrl}
                                    data-testid={dataTestId && `${dataTestId}.incorrect-score.source-url-input`}
                                    onChange={handleUrlChange}
                                    disabled={isSubmitting}
                                    required
                                />
                                {sourceUrlError && (
                                    <div className={styles.errorMessage} data-testid={dataTestId && `${dataTestId}.incorrect-score.error-message`}>
                                        {sourceUrlError}
                                    </div>
                                )}
                            </div>
                        )}

                        {submitError && (
                            <div
                                className={styles.errorMessage}
                                role="alert"
                                data-testid={dataTestId && `${dataTestId}.incorrect-score.submit-error`}
                            >
                                {submitError}
                            </div>
                        )}

                        <div className={styles.buttonContainer}>
                            <div className={styles.buttonWrapper}>
                                <button
                                    type="button"
                                    className={`btn btn-default text-uppercase btn-block ${styles.cancelButton}`}
                                    data-testid={dataTestId && `${dataTestId}.incorrect-score.cancel-btn`}
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className={styles.buttonWrapper}>
                                <button
                                    type="button"
                                    className={`btn btn-primary text-uppercase btn-block ${styles.submitButton} ${(isSubmitting || isSubmitDisabled) ? styles.submitButtonDisabled : ''}`}
                                    data-testid={dataTestId && `${dataTestId}.incorrect-score.submit-btn`}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || isSubmitDisabled}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    </div>
                </Popup>
            )}

            {showSuccess && (
                <Popup
                    className={`popup-md ${styles.popupConfig}`}
                    data-testid={dataTestId && `${dataTestId}.success`}
                    onCloseClick={() => setShowSuccess(false)}
                    config={POPUP_CONFIG}
                >
                    <div className={styles.popupContainer}>
                        <p className={styles.popupHeader} data-testid={dataTestId && `${dataTestId}.success.title`}>
                            {SCORE_UPDATE_SUCCESS_COPY.header}
                        </p>
                        <p className={styles.popupDetails} data-testid={dataTestId && `${dataTestId}.success.message`}>
                            {SCORE_UPDATE_SUCCESS_COPY.details}
                        </p>

                        <div className={styles.centeredButtonContainer}>
                            <button
                                type="button"
                                className={`btn btn-primary text-uppercase ${styles.closeButton}`}
                                data-testid={dataTestId && `${dataTestId}.success.close-btn`}
                                onClick={() => setShowSuccess(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
};

ScoreUpdateRequest.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    resultId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    dataImportTypeId: PropTypes.number,
    onClose: PropTypes.func.isRequired,
    onRequested: PropTypes.func,
    'data-testid': PropTypes.string,
};

ScoreUpdateRequest.defaultProps = {
    dataImportTypeId: null,
    onRequested: null,
    'data-testid': null,
};

export default ScoreUpdateRequest;
